if (!customElements.get('complete-the-look-card')) {
  class CompleteLookCard extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('.js-ctl-form');
      this.submitBtn = this.querySelector('.ctl-atc-btn');
      this.variantInput = this.querySelector('.ctl-variant-id');
      this.imageEl = this.querySelector('.ctl-card__image');
      this.swatches = this.querySelectorAll('.ctl-swatch');
      this.colorValueEl = this.querySelector('.js-ctl-color-value');

      try {
        this.variantImages = JSON.parse(this.dataset.variantImages || '{}');
      } catch (e) {
        this.variantImages = {};
      }

      if (this.form && this.submitBtn) {
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
      }

      if (this.swatches.length) {
        this.swatches.forEach((swatch) => {
          swatch.addEventListener('click', this.handleSwatchClick.bind(this));
        });
      }
    }

    /**
     * Handles swatch click — updates active state, variant ID, image and color label.
     * @param {Event} evt
     */
    handleSwatchClick(evt) {
      const swatch = evt.currentTarget;
      const variantId = swatch.dataset.variantId;
      if (!variantId) return;

      this.swatches.forEach((s) => s.classList.remove('product-block-options__item--active'));
      swatch.classList.add('product-block-options__item--active');

      if (this.variantInput) {
        this.variantInput.value = variantId;
      }

      if (this.colorValueEl && swatch.dataset.color) {
        this.colorValueEl.textContent = swatch.dataset.color;
      }

      const imageUrl = this.variantImages[variantId];
      if (imageUrl && this.imageEl) {
        const imgEl = this.imageEl.querySelector('img');
        if (imgEl) {
          this.imageEl.classList.add('is-swapping');
          const newImg = new Image();
          newImg.onload = () => {
            imgEl.src = imageUrl;
            imgEl.srcset = imageUrl;
            this.imageEl.classList.remove('is-swapping');
          };
          newImg.onerror = () => {
            this.imageEl.classList.remove('is-swapping');
          };
          newImg.src = imageUrl;
        }
      }
    }

    /**
     * Handles add to cart submission.
     * Intentionally does NOT open the cart drawer.
     * @param {Event} evt
     */
    async handleSubmit(evt) {
      evt.preventDefault();
      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);

      const sectionsToUpdate = ['page-header']
        .map((sel) => document.querySelector(sel))
        .filter((el) => el);
      const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId);

      formData.append('sections_url', window.location.pathname);
      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
      }

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const data = await response.json();

        if (data.status) {
          const error = typeof data.description === 'string' ? data.description : data.message;
          this.setError(error);
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.removeAttribute('aria-disabled');
          return;
        }

        if (!response.ok) throw new Error(response.status);

        if (data.sections) {
          sectionsToUpdate.forEach((el) => {
            if (data.sections[el.dataset.sectionId] && el.updateFromCartChange) {
              el.updateFromCartChange(data.sections[el.dataset.sectionId]);
            }
          });
        }

        this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        this.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { variantId: data.variant_id }
        }));

        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.classList.add('is-success');

        setTimeout(() => {
          this.submitBtn.classList.remove('is-success');
          this.submitBtn.removeAttribute('aria-disabled');
        }, 2000);

      } catch (error) {
        console.log(error); // eslint-disable-line
        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.classList.remove('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        this.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true }));
        this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      }
    }

    /**
     * Shows an inline error message.
     * @param {string} error
     */
    setError(error) {
      let errorEl = this.querySelector('.ctl-error');
      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.className = 'ctl-error';
        errorEl.style.cssText = 'color: red; font-size: 1.1rem; margin: 4px 0 0;';
        this.submitBtn.insertAdjacentElement('afterend', errorEl);
      }
      errorEl.textContent = error || 'Something went wrong.';
      setTimeout(() => { errorEl.textContent = ''; }, 3000);
    }
  }

  customElements.define('complete-the-look-card', CompleteLookCard);
}

if (!customElements.get('complete-the-look')) {
  class CompleteLook extends HTMLElement {
    constructor() {
      super();

      this.track = this.querySelector('[data-ctl-track]');
      this.btnPrev = this.querySelector('[data-ctl-prev]');
      this.btnNext = this.querySelector('[data-ctl-next]');

      if (!this.track || !this.btnPrev || !this.btnNext) return;

      this.btnPrev.addEventListener('click', () => {
        this.track.scrollBy({ left: -this.stepSize(), behavior: 'smooth' });
      });

      this.btnNext.addEventListener('click', () => {
        this.track.scrollBy({ left: this.stepSize(), behavior: 'smooth' });
      });

      this.track.addEventListener('scroll', this.updateNav.bind(this), { passive: true });

      const ro = new ResizeObserver(this.updateNav.bind(this));
      ro.observe(this.track);

      this.updateNav();
    }

    /**
     * Returns the scroll distance per click — one card width plus gap.
     * @returns {number}
     */
    stepSize() {
      const first = this.track.firstElementChild;
      if (!first) return 200;
      const gap = parseFloat(window.getComputedStyle(this.track).columnGap || 0);
      return first.getBoundingClientRect().width + gap;
    }

    /**
     * Shows/hides prev and next buttons based on scroll position.
     */
    updateNav() {
      const scrollable = this.track.scrollWidth > this.track.clientWidth + 2;
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;

      this.btnPrev.classList.toggle('is-hidden', !scrollable || atStart);
      this.btnNext.classList.toggle('is-hidden', !scrollable || atEnd);
    }
  }

  customElements.define('complete-the-look', CompleteLook);
}